import * as dayjs from 'dayjs';

import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../dto/dto.js';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DayOfYear } from './day-of-year.js';

export class MonthOfYear extends DataModel {
  static readonly TYPE_ID: string = '97bd6c37ac684eb8a835a180b7e4e8bc';

  public readonly uniqueId: string;

  constructor(public readonly year: number, public readonly month: number) {
    super();
    this.uniqueId = `${year}.${month.toString().padStart(2, '0')}`;
  }

  get $typeId(): string {
    return MonthOfYear.TYPE_ID;
  }

  static fromDate(date: dayjs.Dayjs): MonthOfYear {
    return new MonthOfYear(
        date.year(),
        // In dayjs months start at 0 not 1. The app expects month to start at 1.
        date.month() + 1,
    );
  }

  static fromDayOfYear(date: DayOfYear): MonthOfYear {
    return new MonthOfYear(
        date.year,
        date.month,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): MonthOfYear {
    return new MonthOfYear(
        MapUtils.get<number>(map, 'year'),
        MapUtils.get<number>(map, 'month'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'year': this.year,
      'month': this.month,
    };
  }

  clone(): this {
    return new MonthOfYear(
        this.year,
        this.month,
    ) as this;
  }

  isSame(other: MonthOfYear): boolean {
    if(this === other) {
      return true;
    }

    return other.year === this.year && other.month === this.month;
  }

  toDate(timezone: TimeZone): dayjs.Dayjs {
    return timezone.at(this.year, this.month - 1, 1, 0, 0);
  }

  static dateToUniqueId(date: dayjs.Dayjs): string {
    return date.year.toString() + '.' + date.month.toString().padStart(2, '0');
  }

  static dayOfYearToUniqueId(dayOfYear: DayOfYear): string {
    return dayOfYear.year.toString() + '.' + dayOfYear.month.toString().padStart(2, '0');
  }
}

export class MonthOfYearBuilder extends DataModelBuilder<MonthOfYear> {
  build(context: SerializationContext, map: SerializedData): MonthOfYear {
    return MonthOfYear.fromMap(context, map);
  }
}
